import React, { useEffect, useRef, useState } from 'react';
import { Paper, Tab, Tabs } from '@material-ui/core';

import { catalogApi as api } from 'api/catalog';
import { warehouseApi } from 'api/warehouse';
import { Notifications, TabPanel } from 'shared';

import { BulkUploadItemMaster } from './bulkUpload/BulkUploadItemMaster';
import { CreateItemMaster } from './components/createItemMaster';
import { ViewItemMaster } from './components/viewItemMaster';

function Catalog({ baseUrl }) {
    const [selectedTab, setSelectedTab] = useState('0');
    const handleChange = (event, value) => {
        setSelectedTab(value);
    };

    const [notification, setNotification] = useState({ text: null, level: null });
    const [taxonomy, setTaxonomy] = useState({ l1: [], l2: [], l3: [], l4: [] });
    const [tempZones, setTempZones] = useState({ shipping: [], storage: [], receiving: [] });
    const [facilities, setFacilities] = useState([]);
    const [rows, setRows] = useState([]);
    const [isLoadingItems, setIsLoadingItems] = useState(true);
    const [items, setItems] = useState([]);
    const latestItemsFetchIDRef = useRef(0);
    const latestItemMastersFetchIDRef = useRef(0);

    const getTaxonomy = async () => {
        const promises = [1, 2, 3, 4].map((lvl) => api.getTaxonomyByLevel(lvl, 'TAXONOMY'));
        const res = await Promise.all(promises);
        if (res.every((item) => item.status)) {
            setTaxonomy({
                l1: res[0].data.categories,
                l2: res[1].data.categories,
                l3: res[2].data.categories,
                l4: res[3].data.categories,
            });
        }
    };

    const getTempZones = async () => {
        const res = await api.getItemTempZones();
        if (!res.status) {
            setNotification({ text: 'Failed to get item temp zones', level: 'error' });
            return;
        }

        setTempZones(res.data);
    };

    const getFacilities = async () => {
        const response = await warehouseApi.getFacilities(true);
        if (response.status === false) {
            return;
        }

        setFacilities(response.data.rows.map((item) => ({ name: item.name, id: item.id })));
    };

    const getItems = async (activeOnly = true, { includeRecipeCards = false } = {}) => {
        // Item master arrives over several sequential pages, so two of these can overlap. Only the newest
        // may write state, or an older one finishing later would win.
        const fetchID = latestItemsFetchIDRef.current + 1;
        latestItemsFetchIDRef.current = fetchID;

        setIsLoadingItems(true);
        try {
            const response = await warehouseApi.getItemMaster(activeOnly, true, { includeRecipeCards });

            if (fetchID !== latestItemsFetchIDRef.current) {
                return;
            }

            if (response.status === false) {
                setNotification({ text: `No items found ${response.msg}`, level: 'error' });
                setRows([]);
                return;
            }
            setRows(response.data.rows);
        } finally {
            // Leave the spinner up if a newer fetch is still running.
            if (fetchID === latestItemsFetchIDRef.current) {
                setIsLoadingItems(false);
            }
        }
    };

    const getItemMasters = async () => {
        // Also a multi-page fetch, and the Create Item Master tab re-triggers it on every save, so the same
        // newest-wins guard applies.
        const fetchID = latestItemMastersFetchIDRef.current + 1;
        latestItemMastersFetchIDRef.current = fetchID;

        const itemMasterRes = await api.getItemInformation(true, 'itemMaster');

        if (fetchID !== latestItemMastersFetchIDRef.current) {
            return;
        }

        if (itemMasterRes.status === true) {
            const items = itemMasterRes.data.items.map((item) => ({
                text: `${item.externalSku} (${item.name})`,
                value: item,
            }));
            setItems(items);
        }
    };

    const refreshItemMasterLists = () => {
        getItems(true);
        getItemMasters();
    };

    useEffect(() => {
        getTaxonomy();
        getTempZones();
        getFacilities();
        getItems(true);
        getItemMasters();
    }, []);

    return (
        <Paper>
            <Notifications options={notification} />
            <Tabs onChange={handleChange} value={selectedTab} indicatorColor="primary" textColor="primary" centered>
                <Tab id="ViewItemMaster_Tab" label="View Item Master" value="0" />
                <Tab id="CreateItemMaster_Tab" label="Create Item Master" value="1" />
                <Tab id="BulkUploadItemMaster_Tab" label="Bulk Upload Item Master" value="2" />
            </Tabs>
            <TabPanel selectedTab={selectedTab} index="0">
                <ViewItemMaster
                    taxonomy={taxonomy}
                    tempZones={tempZones}
                    facilities={facilities}
                    rows={rows}
                    isLoadingItems={isLoadingItems}
                    getItems={getItems}
                    items={items}
                    getItemMasters={getItemMasters}
                />
            </TabPanel>
            <TabPanel selectedTab={selectedTab} index="1">
                <CreateItemMaster
                    taxonomy={taxonomy}
                    tempZones={tempZones}
                    facilities={facilities}
                    items={items}
                    getItemMasters={getItemMasters}
                />
            </TabPanel>
            <TabPanel selectedTab={selectedTab} index="2">
                <BulkUploadItemMaster onPersistSuccess={refreshItemMasterLists} />
            </TabPanel>
        </Paper>
    );
}

export { Catalog };
